// src/components/pipsCreatureKeeper/StarterSelector.tsx
import { logger } from '../../../utils/logger';
// 🔧 FIXED: Prevents infinite "Welcoming Companion..." spinner
// ✅ FIXED: Proper error handling and state management

import React, { useState, useEffect } from 'react';
import {
  IonCard,
  IonCardContent,
  IonButton,
  IonSpinner,
  IonBadge,
  IonIcon,
  IonText,
  IonAlert
} from '@ionic/react';
import { checkmark, flame, water, leaf, flash, moon, skull, sunny, diamond, sparkles } from 'ionicons/icons';
import { Creature } from '../../../types/pipsCreatureKeeper';
import { getRandomStarters } from '../../../services/pipsCreatureKeeperTerritoryService';
import './StarterSelector.css';

interface StarterSelectorProps {
  creatures: Creature[];
  onSelect: (creatureId: number) => Promise<void>;
}

const TYPE_ICONS: { [key: string]: string } = {
  Flame: flame,
  Tide: water,
  Bloom: leaf,
  Storm: flash,
  Shadow: moon,
  Spirit: sparkles,
  Light: sunny,
  Stone: diamond,
  Metal: diamond,
  Frost: water
};

const TYPE_COLORS: { [key: string]: string } = {
  Flame: '#FF4500',
  Tide: '#1E90FF',
  Stone: '#696969',
  Storm: '#FFD700',
  Frost: '#87CEEB',
  Bloom: '#32CD32',
  Shadow: '#4B0082',
  Light: '#FFE4B5',
  Metal: '#C0C0C0',
  Spirit: '#DDA0DD'
};

const StarterSelector: React.FC<StarterSelectorProps> = ({ creatures, onSelect }) => {
  const [starters, setStarters] = useState<Creature[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [confirming, setConfirming] = useState(false);
  const [showError, setShowError] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    // Get 3 random monotype creatures
    const randomStarters = getRandomStarters(creatures, 3);
    setStarters(randomStarters);
  }, [creatures]);

  const handleSelect = (creatureId: number) => {
    if (confirming) return; // Prevent selection while confirming
    setSelectedId(creatureId);
  };

  const handleConfirm = async () => {
    if (selectedId === null || confirming) return;

    logger.log('🎯 Confirming starter selection:', selectedId);
    setConfirming(true);

    try {
      // Add timeout protection
      const timeoutPromise = new Promise((_, reject) => {
        setTimeout(() => reject(new Error('Selection timed out - please try again')), 15000);
      });

      await Promise.race([onSelect(selectedId), timeoutPromise]);

      logger.log('✅ Starter selected successfully');
      // Don't reset confirming - let parent component handle navigation
    } catch (error: any) {
      logger.error('❌ Error selecting starter:', error);
      setErrorMessage(error.message || 'Failed to select starter. Please try again.');
      setShowError(true);
      setConfirming(false); // Reset on error so user can try again
    }
  };

  if (starters.length === 0) {
    return (
      <div className="starter-loading">
        <IonSpinner name="crescent" />
        <p>Preparing your starter creatures...</p>
      </div>
    );
  }

  return (
    <div className="starter-selector">
      <div className="starter-header">
        <h2>Choose Your First Companion</h2>
        <p className="starter-subtitle">
          Your choice will influence which creatures you can attract on your first expeditions.
          Choose wisely, Habitat Specialist!
        </p>
      </div>

      <div className="starter-grid">
        {starters.map((creature) => (
          <IonCard
            key={creature.id}
            className={`starter-card ${selectedId === creature.id ? 'selected' : ''} ${confirming ? 'disabled' : ''}`}
            onClick={() => handleSelect(creature.id)}
            button={!confirming}
            style={{ opacity: confirming && selectedId !== creature.id ? 0.5 : 1 }}
          >
            <div className="starter-card-inner">
              {selectedId === creature.id && (
                <div className="selected-badge">
                  <IonIcon icon={checkmark} />
                </div>
              )}

              <div className="starter-image-container">
                {creature.image_url ? (
                  <img
                    src={creature.image_url}
                    alt={creature.name}
                    className="starter-image"
                    onError={(e) => {
                      (e.target as HTMLImageElement).style.display = 'none';
                    }}
                  />
                ) : creature.ascii_art ? (
                  <pre className="starter-ascii-art">{creature.ascii_art}</pre>
                ) : (
                  <div className="starter-placeholder">🦋</div>
                )}
              </div>

              <IonCardContent className="starter-info">
                <h3 className="starter-name">{creature.name}</h3>

                <div className="starter-types">
                  <IonBadge
                    className="type-badge"
                    style={{ backgroundColor: TYPE_COLORS[creature.type1] }}
                  >
                    <IonIcon icon={TYPE_ICONS[creature.type1]} className="type-icon" />
                    {creature.type1}
                  </IonBadge>
                  {creature.type2 && (
                    <IonBadge
                      className="type-badge"
                      style={{ backgroundColor: TYPE_COLORS[creature.type2] }}
                    >
                      <IonIcon icon={TYPE_ICONS[creature.type2]} className="type-icon" />
                      {creature.type2}
                    </IonBadge>
                  )}
                </div>

                <p className="starter-description">
                  {creature.description || 'A mysterious creature waiting to be your companion.'}
                </p>
              </IonCardContent>
            </div>
          </IonCard>
        ))}
      </div>

      <div className="starter-actions">
        {selectedId !== null && (
          <>
            <IonText color="medium" className="starter-confirmation-text">
              <p>
                <strong>{starters.find(c => c.id === selectedId)?.name}</strong> will be your first companion.
                This choice cannot be changed!
              </p>
            </IonText>
            <IonButton
              expand="block"
              size="large"
              color="success"
              onClick={handleConfirm}
              disabled={confirming}
              className="confirm-starter-button"
            >
              {confirming ? (
                <>
                  <IonSpinner name="crescent" style={{ marginRight: '8px' }} />
                  Welcoming Companion...
                </>
              ) : (
                'Confirm Selection'
              )}
            </IonButton>
          </>
        )}

        {selectedId === null && !confirming && (
          <IonText color="medium" className="starter-hint">
            <p>Select a creature to begin your journey</p>
          </IonText>
        )}
      </div>

      {/* Error Alert */}
      <IonAlert
        isOpen={showError}
        onDidDismiss={() => setShowError(false)}
        header="Selection Failed"
        message={errorMessage}
        buttons={['OK']}
      />
    </div>
  );
};

export default StarterSelector;